import { GlobalConfigType } from 'actions'
import { allHeldTypesDeny } from 'config'
import { pagePermissionSubmenus, submMenuMap } from 'pages/Admin/AdminTools/Configuration/Features/constant'
import { store } from 'reducers'

const isGloballyEnabled = (pageKey: string, data: Record<string, boolean> = {}, custom: Record<string, boolean> = {}) =>
  data[pageKey] !== false && custom[pageKey] !== false

const getUserPagePermissionDenies = (auth: any, pagePermissions: any): string[] => {
  if (Array.isArray(pagePermissions?.userDenies)) return pagePermissions.userDenies
  // getUserPermissions response is stored as auth.role by setUserPermissions
  const fromPermissions = auth?.role?.pagePermissionDenies
  if (Array.isArray(fromPermissions)) return fromPermissions
  return []
}

export const hasPagePermissions = (pageIds: string[] = [], pageParams: Record<string, any> = {}) => {
  const { globalConfigReducer, auth } = store.getState() as any
  const pagePermissions = globalConfigReducer[GlobalConfigType.PagePermissions] || {}
  const { data, custom = {}, byAccountType = {} } = pagePermissions
  const userDenies = getUserPagePermissionDenies(auth, pagePermissions)

  if (!pageIds || !pageIds.length || (!data && !custom && !byAccountType && !userDenies.length)) return true

  let hasPermission = true
  let pageKey = ''

  pageIds.forEach((pageId) => {
    if (!hasPermission) return
    if (pageId.startsWith(':')) pageId = pageParams[pageId.substring(1, pageParams.length)]

    pageKey += `-${pageId}`
    if (data && data[pageKey] === false) hasPermission = false
    if (custom && custom[pageKey] === false) hasPermission = false
    // Account-type override can only further restrict; never enable a globally disabled page.
    // Deny only when every held role explicitly denies this page key.
    if (allHeldTypesDeny(auth?.profile, byAccountType, pageKey)) hasPermission = false
    // User override can only further restrict.
    if (userDenies.includes(pageKey)) hasPermission = false
  })

  return hasPermission
}

export const removeNonPagePermissions = (
  menus: Record<string, string>,
  pageIds: string[],
  isSubmenu: boolean = false,
) => {
  Object.keys(menus).forEach((menu) => {
    if (!hasPagePermissions([...pageIds, ':menu'], { menu })) delete menus[menu]
  })

  if (isSubmenu) {
    const availablePagePermissions = getAvailablePagePermissions()
    Object.keys(menus).map((key) => {
      if (!availablePagePermissions.includes(key)) delete menus[key]
    })
  }

  return menus
}

export const getAvailablePagePermissions = () => {
  const { globalConfigReducer, auth } = store.getState() as any
  const pagePermissions = globalConfigReducer[GlobalConfigType.PagePermissions] || {}
  const { data = {}, custom = {}, byAccountType = {} } = pagePermissions
  const profile = auth?.profile
  const userDenies = getUserPagePermissionDenies(auth, pagePermissions)

  let rtl: string[] = []
  Object.keys(pagePermissionSubmenus).map((key) => {
    const pagePermissionKey = `-pipeline-${key}`
    if (
      isGloballyEnabled(pagePermissionKey, data, custom) &&
      !allHeldTypesDeny(profile, byAccountType, pagePermissionKey) &&
      !userDenies.includes(pagePermissionKey)
    ) {
      rtl.push(...pagePermissionSubmenus[key])
    }
  })

  Object.keys(data).map((key) => {
    const params = key.split('-')
    if (params[1] === 'pipeline' && params.length > 3 && data[key] === false) {
      rtl = rtl.filter((item) => item !== submMenuMap[params[3]])
    }
  })
  Object.keys(custom).map((key) => {
    const params = key.split('-')
    if (params[1] === 'pipeline' && params.length > 3 && custom[key] === false) {
      rtl = rtl.filter((item) => item !== submMenuMap[params[3]])
    }
  })
  // Drop a pipeline submenu only when every held role denies that page key.
  const deniedPageKeys = new Set<string>()
  Object.values(byAccountType || {}).forEach((overrides) => {
    Object.keys(overrides || {}).forEach((key) => {
      if (overrides[key] === false) deniedPageKeys.add(key)
    })
  })
  deniedPageKeys.forEach((key) => {
    if (!allHeldTypesDeny(profile, byAccountType, key)) return
    const params = key.split('-')
    if (params[1] === 'pipeline' && params.length > 3) {
      rtl = rtl.filter((item) => item !== submMenuMap[params[3]])
    }
  })
  userDenies.forEach((key) => {
    const params = key.split('-')
    if (params[1] === 'pipeline' && params.length > 3) {
      rtl = rtl.filter((item) => item !== submMenuMap[params[3]])
    }
  })
  return rtl
}

/** True when the page is on in global Page Permissions (data + custom). */
export const isPageGloballyEnabled = (pageKey: string) => {
  const { globalConfigReducer } = store.getState() as any
  const { data = {}, custom = {} } = globalConfigReducer[GlobalConfigType.PagePermissions] || {}
  return isGloballyEnabled(pageKey, data, custom)
}
